@if (open()) {
	<div class="app-settings-backdrop" (click)="close()">
		<section
			class="app-settings-dialog"
			role="dialog"
			aria-modal="true"
			aria-labelledby="app-settings-title"
			aria-describedby="app-settings-description"
			tabindex="-1"
			(click)="$event.stopPropagation()"
			(keydown.escape)="close()">
			<header class="app-settings-dialog__header">
				<div>
					<p class="app-settings-dialog__eyebrow">Global settings</p>
					<h2 id="app-settings-title">Application settings</h2>
					<p id="app-settings-description" class="app-settings-dialog__description">
						Central place for visualization and future application-wide controls.
					</p>
				</div>
				<button class="app-settings-dialog__close" type="button" aria-label="Close settings" (click)="close()">
					×
				</button>
			</header>

			<div class="app-settings-dialog__layout">
				<nav class="app-settings-nav" aria-label="Settings categories">
					@for (section of sections; track section.id) {
						<button
							type="button"
							class="app-settings-nav__item"
							[class.app-settings-nav__item--active]="activeSection() === section.id"
							(click)="selectSection(section.id)">
							<span class="app-settings-nav__label">{{ section.label }}</span>
							<span class="app-settings-nav__description">{{ section.description }}</span>
						</button>
					}
				</nav>

				<div class="app-settings-panel">
					@if (activeSection() === 'visualization') {
						<section class="app-settings-card" aria-labelledby="antenna-visualization-title">
							<div class="app-settings-card__header">
								<div>
									<p class="app-settings-card__eyebrow">Visualization</p>
									<h3 id="antenna-visualization-title">Antenna lobe visualization</h3>
								</div>
								<p class="app-settings-card__summary">
									Controls the default runtime width, height, and reach of generated sector lobes.
								</p>
							</div>

							@if (loading()) {
								<p class="app-settings-status app-settings-status--info">Loading current settings…</p>
							} @else {
								<div class="app-settings-grid">
									<label class="app-settings-field">
										<span class="app-settings-field__label">Minimum boresight length (m)</span>
										<input #visualizationFirstField type="number" step="0.1" [formField]="settingsForm.minimum_boresight_length_m" />
										<span class="app-settings-field__help">Shortest visual reach for low-power sectors.</span>
										@if (settingsForm.minimum_boresight_length_m().touched() && settingsForm.minimum_boresight_length_m().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a valid minimum boresight length.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Maximum boresight length (m)</span>
										<input type="number" step="0.1" [formField]="settingsForm.maximum_boresight_length_m" />
										<span class="app-settings-field__help">Longest visual reach for high-power sectors.</span>
										@if (settingsForm.maximum_boresight_length_m().touched() && settingsForm.maximum_boresight_length_m().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a valid maximum boresight length.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Maximum ground-touch length (m)</span>
										<input type="number" step="0.1" [formField]="settingsForm.maximum_ground_touch_length_m" />
										<span class="app-settings-field__help">Upper bound when tilt drives the lobe toward the ground plane.</span>
										@if (settingsForm.maximum_ground_touch_length_m().touched() && settingsForm.maximum_ground_touch_length_m().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a valid ground-touch length.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Minimum power (dBm)</span>
										<input type="number" step="0.1" [formField]="settingsForm.minimum_power_dbm" />
										<span class="app-settings-field__help">Lower end of the power-to-length normalization range.</span>
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Maximum power (dBm)</span>
										<input type="number" step="0.1" [formField]="settingsForm.maximum_power_dbm" />
										<span class="app-settings-field__help">Upper end of the power-to-length normalization range.</span>
										@if (settingsForm.maximum_power_dbm().touched() && settingsForm.maximum_power_dbm().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a valid maximum power.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Horizontal width ratio</span>
										<input type="number" step="0.01" [formField]="settingsForm.horizontal_width_ratio" />
										<span class="app-settings-field__help">Controls azimuth spread relative to boresight length.</span>
										@if (settingsForm.horizontal_width_ratio().touched() && settingsForm.horizontal_width_ratio().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a horizontal ratio between 0 and 1.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Vertical width ratio</span>
										<input type="number" step="0.01" [formField]="settingsForm.vertical_width_ratio" />
										<span class="app-settings-field__help">Controls elevation spread relative to boresight length.</span>
										@if (settingsForm.vertical_width_ratio().touched() && settingsForm.vertical_width_ratio().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a vertical ratio between 0 and 1.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Ground clearance (m)</span>
										<input type="number" step="0.1" [formField]="settingsForm.ground_clearance_m" />
										<span class="app-settings-field__help">Keeps the lobe volume from dipping below grade.</span>
										@if (settingsForm.ground_clearance_m().touched() && settingsForm.ground_clearance_m().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a non-negative ground clearance.' }}</span>
										}
									</label>
								</div>
							}
						</section>

						<section class="app-settings-card" aria-labelledby="subscriber-marker-title">
							<div class="app-settings-card__header">
								<div>
									<p class="app-settings-card__eyebrow">Visualization</p>
									<h3 id="subscriber-marker-title">Subscriber marker asset</h3>
								</div>
								<p class="app-settings-card__summary">
									Controls the default UE marker model and scale used by the Three.js scene viewer.
								</p>
							</div>

							@if (loading()) {
								<p class="app-settings-status app-settings-status--info">Loading current settings…</p>
							} @else {
								<div class="app-settings-grid">
									<label class="app-settings-field">
										<span class="app-settings-field__label">UE asset</span>
										<select [value]="subscriberMarkerSettingsModel().asset_name" (change)="subscriberMarkerSettingsModel.set({ ...subscriberMarkerSettingsModel(), asset_name: $any($event.target).value })">
											<option value="">Built-in cube marker</option>
											@for (model of availableModels(); track model.id) {
												<option [value]="model.file">{{ model.name }} ({{ model.file }})</option>
											}
										</select>
										<span class="app-settings-field__help">GLB or glTF served by the backend models endpoint. The generated default is `subscriber_handset.glb`.</span>
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Scale X (m)</span>
										<input type="number" step="0.1" [formField]="subscriberMarkerSettingsForm.scale_x_m" />
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Scale Y (m)</span>
										<input type="number" step="0.1" [formField]="subscriberMarkerSettingsForm.scale_y_m" />
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Scale Z (m)</span>
										<input type="number" step="0.1" [formField]="subscriberMarkerSettingsForm.scale_z_m" />
									</label>

									<label class="app-settings-field app-settings-field--toggle">
										<span class="app-settings-field__label">Show all subscribers by default</span>
										<input type="checkbox" [checked]="subscriberMarkerSettingsModel().show_all_by_default" (change)="subscriberMarkerSettingsModel.set({ ...subscriberMarkerSettingsModel(), show_all_by_default: $any($event.target).checked })" />
										<span class="app-settings-field__help">Automatically render all exported subscriber markers and their paths when the viewer opens.</span>
									</label>
								</div>
							}
						</section>
					} @else if (activeSection() === 'simulation') {
						<section class="app-settings-card" aria-labelledby="simulation-settings-title">
							<div class="app-settings-card__header">
								<div>
									<p class="app-settings-card__eyebrow">Simulation</p>
									<h3 id="simulation-settings-title">Radio map solver defaults</h3>
								</div>
								<p class="app-settings-card__summary">
									Controls shared worker defaults used for baseline and subsequent Sionna RT coverage runs.
								</p>
							</div>

							@if (loading()) {
								<p class="app-settings-status app-settings-status--info">Loading current settings…</p>
							} @else {
								<div class="app-settings-grid">
									<label class="app-settings-field">
										<span class="app-settings-field__label">Frequency (GHz)</span>
										<input #simulationFirstField type="number" step="0.1" [formField]="simulationSettingsForm.radio_map_frequency_ghz" />
										<span class="app-settings-field__help">Global carrier frequency for VSUB radio-map runs. Constrained to the ITU material-model range of `1-100 GHz`.</span>
										@if (simulationSettingsForm.radio_map_frequency_ghz().touched() && simulationSettingsForm.radio_map_frequency_ghz().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a frequency between 1 and 100 GHz.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Bandwidth (Hz)</span>
										<input type="number" step="100000" [formField]="simulationSettingsForm.radio_map_bandwidth_hz" />
										<span class="app-settings-field__help">Channel bandwidth used for the thermal-noise term in SINR. The Verizon LTE default here is `10 MHz`.</span>
										@if (simulationSettingsForm.radio_map_bandwidth_hz().touched() && simulationSettingsForm.radio_map_bandwidth_hz().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a bandwidth between 100 kHz and 100 MHz.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Temperature (K)</span>
										<input type="number" step="1" [formField]="simulationSettingsForm.radio_map_temperature_k" />
										<span class="app-settings-field__help">Receiver thermal-noise temperature assumption. `290 K` is the standard room-temperature planning baseline.</span>
										@if (simulationSettingsForm.radio_map_temperature_k().touched() && simulationSettingsForm.radio_map_temperature_k().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a temperature between 200 K and 400 K.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Receiver noise figure (dB)</span>
										<input type="number" step="0.1" [formField]="simulationSettingsForm.radio_map_receiver_noise_figure_db" />
										<span class="app-settings-field__help">Additional receiver implementation noise above the thermal floor. The current Verizon LTE default is `7 dB`.</span>
										@if (simulationSettingsForm.radio_map_receiver_noise_figure_db().touched() && simulationSettingsForm.radio_map_receiver_noise_figure_db().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a receiver noise figure between 0 and 20 dB.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Radio map cell size (m)</span>
										<input type="number" step="0.1" [formField]="simulationSettingsForm.radio_map_cell_size_m" />
										<span class="app-settings-field__help">Global grid spacing for the radio map solver. `4` means a `4 x 4 m` sampling grid.</span>
										@if (simulationSettingsForm.radio_map_cell_size_m().touched() && simulationSettingsForm.radio_map_cell_size_m().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a positive cell size.' }}</span>
										}
									</label>

									<label class="app-settings-field app-settings-field--toggle">
										<span class="app-settings-field__label">Enable diffraction</span>
										<input type="checkbox" [formField]="simulationSettingsForm.radio_map_diffraction_enabled" />
										<span class="app-settings-field__help">Includes diffracted propagation paths in the VSUB radio-map solve. Useful in NLOS urban cases, but it increases runtime.</span>
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Radio map max depth</span>
										<input type="number" step="1" [formField]="simulationSettingsForm.radio_map_max_depth" />
										<span class="app-settings-field__help">Upper bound for reflection and diffraction recursion in the Sionna RT solve.</span>
										@if (simulationSettingsForm.radio_map_max_depth().touched() && simulationSettingsForm.radio_map_max_depth().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a max depth between 1 and 12.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Samples per TX</span>
										<input type="number" step="1000" [formField]="simulationSettingsForm.radio_map_samples_per_tx" />
										<span class="app-settings-field__help">Ray sampling budget per transmitter. Higher values improve stability but cost runtime.</span>
										@if (simulationSettingsForm.radio_map_samples_per_tx().touched() && simulationSettingsForm.radio_map_samples_per_tx().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter at least 1,000 samples per transmitter.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Solve plane height above ground (m)</span>
										<input type="number" step="0.1" [formField]="simulationSettingsForm.radio_map_plane_height_m" />
										<span class="app-settings-field__help">Height of the receiver solve plane above the local ground plane for the radio map.</span>
										@if (simulationSettingsForm.radio_map_plane_height_m().touched() && simulationSettingsForm.radio_map_plane_height_m().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a non-negative plane height.' }}</span>
										}
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Minimum solve extent (m)</span>
										<input type="number" step="1" [formField]="simulationSettingsForm.radio_map_min_extent_m" />
										<span class="app-settings-field__help">Prevents tiny bboxes from collapsing into an unusably small solve plane.</span>
										@if (simulationSettingsForm.radio_map_min_extent_m().touched() && simulationSettingsForm.radio_map_min_extent_m().errors()[0]; as error) {
											<span class="app-settings-field__error">{{ error.message ?? 'Enter a positive minimum solve extent.' }}</span>
										}
									</label>
								</div>
							}
						</section>

						<section class="app-settings-card" aria-labelledby="stage-a-guardrails-title">
							<div class="app-settings-card__header">
								<div>
									<p class="app-settings-card__eyebrow">Simulation guardrails</p>
									<h3 id="stage-a-guardrails-title">Stage A runpod admission limits</h3>
								</div>
								<p class="app-settings-card__summary">
									Controls the deny-only Stage A thresholds used before queuing `runpod_serverless` Stage B work.
								</p>
							</div>

							@if (loading()) {
								<p class="app-settings-status app-settings-status--info">Loading current settings…</p>
							} @else {
								<div class="app-settings-grid">
									<label class="app-settings-field app-settings-field--toggle">
										<span class="app-settings-field__label">Enable runpod_serverless guardrails</span>
										<input
											type="checkbox"
											[checked]="stageAGuardrailSettingsModel().runpod_serverless.enabled"
											(change)="toggleStageAGuardrailRunpodEnabled($any($event.target).checked)" />
										<span class="app-settings-field__help">When enabled, Stage A can deny downstream RunPod dispatch instead of letting oversized jobs continue.</span>
									</label>

									<div class="app-settings-field app-settings-field--static">
										<span class="app-settings-field__label">Policy mode</span>
										<div class="app-settings-static-value">Deny only</div>
										<span class="app-settings-field__help">Phase 1 does not reroute jobs. It either allows or denies `runpod_serverless` dispatch.</span>
									</div>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Max estimated VRAM (GB)</span>
										<input
											type="number"
											step="0.1"
											min="0.1"
											[value]="stageAGuardrailSettingsModel().runpod_serverless.max_estimated_total_vram_gb"
											(input)="updateStageAGuardrailRunpodNumber('max_estimated_total_vram_gb', +$any($event.target).value)" />
										<span class="app-settings-field__help">Denies jobs when the Stage A heuristic estimate exceeds this limit.</span>
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">PathSolver complexity limit</span>
										<input
											type="number"
											step="1"
											min="1"
											[value]="stageAGuardrailSettingsModel().runpod_serverless.pathsolver_complexity_limit"
											(input)="updateStageAGuardrailRunpodNumber('pathsolver_complexity_limit', +$any($event.target).value)" />
										<span class="app-settings-field__help">Uses the same `meshes * tx * rx * depth` budget that Stage B PathSolver preflight enforces on serverless runs.</span>
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Max subscribers without batching</span>
										<input
											type="number"
											step="1"
											min="1"
											[value]="stageAGuardrailSettingsModel().runpod_serverless.max_subscribers_no_batch"
											(input)="updateStageAGuardrailRunpodNumber('max_subscribers_no_batch', +$any($event.target).value)" />
										<span class="app-settings-field__help">Above this count, Stage A will record that Stage B batching is required and deny direct RunPod dispatch.</span>
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Recommended subscriber batch size</span>
										<input
											type="number"
											step="1"
											min="1"
											[value]="stageAGuardrailSettingsModel().runpod_serverless.recommended_subscriber_batch_size"
											(input)="updateStageAGuardrailRunpodNumber('recommended_subscriber_batch_size', +$any($event.target).value)" />
										<span class="app-settings-field__help">Suggested subscriber chunk size for the later batching rollout.</span>
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Max face count</span>
										<input
											type="number"
											step="1"
											min="1"
											[value]="stageAGuardrailSettingsModel().runpod_serverless.max_face_count_total"
											(input)="updateStageAGuardrailRunpodNumber('max_face_count_total', +$any($event.target).value)" />
										<span class="app-settings-field__help">Upper bound for total scene faces exported by Stage A.</span>
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Max scene dict pickle bytes</span>
										<input
											type="number"
											step="1"
											min="1"
											[value]="stageAGuardrailSettingsModel().runpod_serverless.max_scene_dict_pickle_bytes"
											(input)="updateStageAGuardrailRunpodNumber('max_scene_dict_pickle_bytes', +$any($event.target).value)" />
										<span class="app-settings-field__help">Serialized geometry scene size limit. `134217728` is approximately `128 MiB`.</span>
									</label>

									<label class="app-settings-field">
										<span class="app-settings-field__label">Max bbox area (m²)</span>
										<input
											type="number"
											step="1"
											min="1"
											[value]="stageAGuardrailSettingsModel().runpod_serverless.max_bbox_area_m2"
											(input)="updateStageAGuardrailRunpodNumber('max_bbox_area_m2', +$any($event.target).value)" />
										<span class="app-settings-field__help">Upper bound for the projected Stage A solve area before RunPod dispatch is denied.</span>
									</label>
								</div>

								@if (stageAGuardrailValidationError(); as error) {
									<p class="app-settings-status app-settings-status--error">{{ error }}</p>
								}
							}
						</section>
					} @else {
						<section class="app-settings-card" aria-labelledby="census-ingest-title">
							<div class="app-settings-card__header">
								<div>
									<p class="app-settings-card__eyebrow">Data ingest</p>
									<h3 id="census-ingest-title">US Census state blocks</h3>
								</div>
								<p class="app-settings-card__summary">
									Download and persist 2020 decennial Census block polygons for a US state. This is the backend ingest step that will feed the future census-based demand model.
								</p>
							</div>

							<div class="app-settings-data-action app-settings-data-action--neutral">
								<div class="app-settings-grid app-settings-grid--data">
									<label class="app-settings-field">
										<span class="app-settings-field__label">State code</span>
										<input
											type="text"
											maxlength="2"
											placeholder="MA"
											[value]="censusStateCode()"
											(input)="updateCensusStateCode($any($event.target).value)" />
										<span class="app-settings-field__help">Use a two-letter US state abbreviation such as MA, TX, NC, or DC.</span>
										@if (censusStateCodeError(); as error) {
											<span class="app-settings-field__error">{{ error }}</span>
										}
									</label>

									<div class="app-settings-field app-settings-field--static">
										<span class="app-settings-field__label">Dataset</span>
										<div class="app-settings-static-value">2020 decennial Census blocks</div>
										<span class="app-settings-field__help">Phase 1 is intentionally limited to the finest nationwide Census geography with baked-in 2020 block population counts.</span>
									</div>
								</div>

								<div class="app-settings-data-actions-row">
									<button
										type="button"
										class="app-settings-btn app-settings-btn--primary"
										(click)="ingestCensusState()"
										[disabled]="censusSubmitting() || censusStateCodeError() !== null">
										{{ censusSubmitting() ? 'Ingesting…' : 'Ingest Census State' }}
									</button>
									@if (censusJobId(); as jobId) {
										<p class="app-settings-data-job-id">Job {{ jobId.slice(0, 8) }}</p>
									}
								</div>

								@if (censusJobStatusLabel(); as statusLabel) {
									<p class="app-settings-status app-settings-status--info">{{ statusLabel }}</p>
								}
								@if (censusResultSummary(); as summary) {
									<p class="app-settings-status app-settings-status--success">{{ summary }}</p>
								}
							</div>
						</section>

						<section class="app-settings-card" aria-labelledby="data-maintenance-title">
							<div class="app-settings-card__header">
								<div>
									<p class="app-settings-card__eyebrow">Data maintenance</p>
									<h3 id="data-maintenance-title">Verizon synth pre/post lineage</h3>
								</div>
								<p class="app-settings-card__summary">
									Permanently delete app-facing cell_towers rows with <code>data_source=verizon_synth_prepost</code> when no QoS project snapshot still depends on them.
								</p>
							</div>

							<div class="app-settings-data-action">
								<p class="app-settings-data-action__copy">
									Use this when you need to clear the synthetic Verizon lineage from the shared tower table before a fresh import or propagate run. The backend blocks the delete if any project still contains snapshot rows from that lineage.
								</p>
								<button
									type="button"
									class="app-settings-btn app-settings-btn--danger"
									(click)="openPurgeVerizonSynthPrepostConfirm()"
									[disabled]="nukingDataSource() || checkingPurgeStatus()">
									{{ checkingPurgeStatus() ? 'Checking…' : (nukingDataSource() ? 'Deleting…' : 'Nuke verizon_synth_prepost rows') }}
								</button>
							</div>
						</section>

						<section class="app-settings-card" aria-labelledby="job-cleanup-title">
							<div class="app-settings-card__header">
								<div>
									<p class="app-settings-card__eyebrow">Data maintenance</p>
									<h3 id="job-cleanup-title">Job and artifact cleanup</h3>
								</div>
								<p class="app-settings-card__summary">
									Estimate and delete old terminal jobs from <code>backend/data/jobs</code>. Jobs in QoS scenario/project chains are protected and excluded.
								</p>
							</div>

							<div class="app-settings-data-action">
								<div class="app-settings-grid app-settings-grid--data app-settings-grid--job-cleanup">
									<label class="app-settings-field">
										<span class="app-settings-field__label">Delete jobs</span>
										<select
											[value]="jobsCleanupRetention()"
											(change)="updateJobsCleanupRetention($any($event.target).value)">
											@for (option of jobsCleanupRetentionOptions; track option.value) {
												<option [value]="option.value">{{ option.label }}</option>
											}
										</select>
										<span class="app-settings-field__help">The estimate and nuke action are scoped to this retention window.</span>
									</label>

									<div class="app-settings-data-actions-row">
										<button
											type="button"
											class="app-settings-btn app-settings-btn--secondary"
											(click)="refreshJobsCleanupEstimate()"
											[disabled]="checkingJobsCleanupEstimate() || nukingOldJobs()">
											{{ checkingJobsCleanupEstimate() ? 'Estimating…' : 'Estimate reclaimable space' }}
										</button>
										<button
											type="button"
											class="app-settings-btn app-settings-btn--danger"
											(click)="openNukeOldJobsConfirm()"
											[disabled]="checkingJobsCleanupEstimate() || nukingOldJobs()">
											{{ nukingOldJobs() ? 'Deleting…' : 'Nuke old jobs + artifacts' }}
										</button>
									</div>
								</div>

								@if (jobsCleanupEstimate(); as estimate) {
									<div class="app-settings-job-cleanup-estimate" role="status" aria-live="polite">
										@if (jobsCleanupTargetLabel(); as targetLabel) {
											<p class="app-settings-job-cleanup-estimate__target">Target: <strong>{{ targetLabel }}</strong></p>
										}
										<p>
											Deletable jobs: <strong>{{ estimate.deletable_jobs }}</strong>
											| Protected chain jobs: <strong>{{ estimate.protected_referenced_jobs }}</strong>
											| Projects referencing jobs: <strong>{{ estimate.projects_with_referenced_jobs || 0 }}</strong>
											| Referenced baseline jobs: <strong>{{ estimate.referenced_baseline_jobs || 0 }}</strong>
											| Estimated reclaim: <strong>{{ estimate.estimated_reclaimable_human }}</strong>
											@if (estimate.garage_configured) {
												| Garage objects: <strong>{{ estimate.garage_object_count || 0 }}</strong>
												| Garage reclaim: <strong>{{ estimate.garage_reclaimable_human || '0 B' }}</strong>
											} @else {
												| Garage reclaim: <strong>not configured</strong>
											}
										</p>
									</div>
								}
							</div>
						</section>
					}

					@if (loadError(); as error) {
						<p class="app-settings-status app-settings-status--error">{{ error }}</p>
					}
					@if (saveError(); as error) {
						<p class="app-settings-status app-settings-status--error">{{ error }}</p>
					}
					@if (saveNotice(); as notice) {
						<p class="app-settings-status app-settings-status--success">{{ notice }}</p>
					}

					<footer class="app-settings-dialog__footer">
						<p class="app-settings-dialog__footer-note">
							{{ footerNote() }}
						</p>
						<div class="app-settings-dialog__actions">
							<button type="button" class="app-settings-btn app-settings-btn--secondary" (click)="close()" [disabled]="saving()">
								Close
							</button>
							@if (sectionHasForm()) {
								<button type="button" class="app-settings-btn app-settings-btn--secondary" (click)="resetDraft()" [disabled]="saving() || !hasUnsavedChanges()">
									Reset draft
								</button>
								<button type="button" class="app-settings-btn app-settings-btn--primary" (click)="save()" [disabled]="loading() || saving() || formInvalid()">
									{{ saving() ? 'Saving…' : 'Save settings' }}
								</button>
							}
						</div>
					</footer>
				</div>
			</div>
		</section>
	</div>

	@if (purgeConfirmOpen()) {
		<app-confirm-dialog
			title="Delete Verizon Synth Lineage"
			[message]="'Delete ' + (purgeStatus()?.matching_cell_towers ?? 0) + ' cell_towers row(s) with data_source \'verizon_synth_prepost\'? Project snapshots are not touched.'"
			confirmLabel="Delete rows"
			cancelLabel="Cancel"
			[destructive]="true"
			(confirmed)="purgeVerizonSynthPrepost()"
			(cancelled)="cancelPurgeVerizonSynthPrepost()"/>
	}

	@if (nukeJobsConfirmOpen()) {
		<app-confirm-dialog
			title="Delete Old Jobs"
			[message]="jobsCleanupConfirmMessage()"
			confirmLabel="Nuke jobs"
			cancelLabel="Cancel"
			[destructive]="true"
			(confirmed)="nukeOldJobs()"
			(cancelled)="cancelNukeOldJobs()"/>
	}
}
